/// <amd-module name="Feed/Legacy/Protocols/UPack/UPackPackageProtocol" />
import * as React from "react";

import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { getServiceLocation } from "VSS/Platform/Location";

import { TfsServiceInstanceId } from "Feed/Common/Constants/Constants";
import { FeedRole, FeedView, Package, PackageMetrics, PackageVersion, PackageVersionMetrics } from "Feed/Common/Contracts/Feed";
import { UpstreamingBehavior } from "Feed/Common/Protocols/Shared/Contracts/PackagingShared";
import * as PackageResources from "Feed/Common/Resources";
import { FabricIconProps } from "Feed/Common/Utils/Icons";
import { ProtocolCommands } from "Feed/Legacy/Common/ProtocolCommands";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { isV2Feed } from "Feed/Legacy/Helpers/FeedCapabilityHelper";
import * as PermissionHelper from "Feed/Legacy/Helpers/PermissionHelper";
import { BatchGenerator, IBatchIterator, TransformingBatchGenerator } from "Feed/Legacy/Protocols/Common/BatchHandling";
import { DeleteCommandHelper } from "Feed/Legacy/Protocols/Common/DeleteCommandHelper";
import { IPackageDetailsProps } from "Feed/Legacy/Protocols/Common/IPackageDetailsProps";
import { IPackageProtocol, IPackageProtocolClient, isPackage } from "Feed/Legacy/Protocols/Common/IPackageProtocol";
import { ISupportedFeatures } from "Feed/Legacy/Protocols/Common/ISupportedFeatures";
import { PromotePackageCommand, PromotePackagesCommand } from "Feed/Legacy/Protocols/Common/PackageCommands";
import { MinimalPackageDetails } from "Feed/Legacy/Protocols/Common/WebApi/PackagingShared.Contracts";
import { UPackClientTool } from "Feed/Legacy/Protocols/UPack/UPackClientTool";
import { UPackKey } from "Feed/Legacy/Protocols/UPack/UPackConstants";
import { IFeedUPackDataService } from "Feed/Legacy/Protocols/UPack/UPackDataService";
import { UPackOverviewAttributesPanel } from "Feed/Legacy/Protocols/UPack/UPackOverviewAttributesPanel";
import { UPackOverviewContentPanel } from "Feed/Legacy/Protocols/UPack/UPackOverviewContentPanel";
import { IPackageCommand } from "Feed/Legacy/Types/IPackageCommand";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export class UPackPackageProtocol implements IPackageProtocol {
    private static readonly _icon = new FabricIconProps("Package");

    public readonly name = "UPack";
    public readonly key = "UPack";
    get vssIconProps() {
        return UPackPackageProtocol._icon;
    }
    // tslint:disable:no-bitwise
    public supportedCommandsMask: ProtocolCommands =
        ProtocolCommands.Promote |
        ProtocolCommands.Delete |
        ProtocolCommands.Download |
        ProtocolCommands.Restore |
        ProtocolCommands.PermanentDelete;
    public readonly clients: IPackageProtocolClient[] = [];

    private pageContext: IVssPageContext;

    constructor(pageContext: IVssPageContext) {
        this.pageContext = pageContext;
        this.clients.push(new UPackClientTool(this.pageContext, this.getPublishCommand));
    }

    get UPackDataService(): IFeedUPackDataService {
        return this.pageContext.getService<IFeedUPackDataService>("IFeedUPackDataService");
    }

    public getPackageMessage(packageSummary: Package, packageVersion: PackageVersion): string {
        if (packageVersion && !packageVersion.isListed) {
            if (packageVersion.isDeleted) {
                return Utils_String.format(
                    PackageResources.PackageDeletedMessage,
                    packageVersion.version,
                    packageSummary.name
                );
            } else {
                return Utils_String.format(
                    PackageResources.PackageUnlistedMessage,
                    packageVersion.version,
                    packageSummary.name
                );
            }
        }

        return null;
    }

    public getOverviewAttributesPanel(
        feed: Feed,
        packageSummary: Package,
        packageVersion: PackageVersion,
        packageVersionMetrics: PackageVersionMetrics,
        packageMetrics: PackageMetrics,
        isSmartDependenciesEnabled: boolean,
        isProvenanceEnabled: boolean,
        isMetricsEnabled: boolean,
        isMetricsLoading: boolean
    ): React.ReactNode {
        return React.createElement(UPackOverviewAttributesPanel, {
            pageContext: this.pageContext,
            feed,
            packageSummary,
            packageVersion,
            protocol: this,
            packageVersionMetrics,
            packageMetrics,
            isSmartDependenciesEnabled,
            isProvenanceEnabled,
            isMetricsEnabled,
            isMetricsLoading
        } as IPackageDetailsProps);
    }

    public getOverviewContentPanel(
        feed: Feed,
        feedViews: FeedView[],
        packageSummary: Package,
        packageVersion: PackageVersion
    ): React.ReactNode {
        return React.createElement(UPackOverviewContentPanel, {
            pageContext: this.pageContext,
            feed,
            feedViews,
            packageSummary,
            packageVersion,
            protocol: this
        } as IPackageDetailsProps);
    }

    public getMultiSelectPackageCommands(
        feed: Feed,
        selectedPackages?: Package[],
        selectedVersions?: PackageVersion[]
    ): IPackageCommand[] {
        const packageCommands = new Array<IPackageCommand>();

        const role: FeedRole = PermissionHelper.getUsersRoleForFromFeed(feed);
        const showDisabledCommands = role === FeedRole.None || role === FeedRole.Reader;

        // Trim out npm cached packages for V1 feeds. (Must do it here as well because there is a chance of mixed protocols)
        let cachedPackagesIncluded = false;
        if (!isV2Feed(feed)) {
            if (selectedPackages !== null) {
                const selectedPackagesWithoutCached: Package[] = selectedPackages.filter(
                    (pkg: Package) => pkg.isCached !== true && pkg.versions[0].isCachedVersion !== true
                );
                if (selectedPackagesWithoutCached.length < selectedPackages.length) {
                    cachedPackagesIncluded = true;
                    selectedPackages = selectedPackagesWithoutCached;
                }
            } else if (selectedVersions !== null) {
                const selectedVersionsWithoutCached: PackageVersion[] = selectedVersions.filter(
                    (version: PackageVersion) => version.isCachedVersion !== true
                );
                if (selectedVersionsWithoutCached.length < selectedVersions.length) {
                    cachedPackagesIncluded = true;
                    selectedVersions = selectedVersionsWithoutCached;
                }
            }
        }

        if (selectedPackages && selectedPackages.length > 0) {
            // Promote
            // Only add Promote button on package list for v1
            packageCommands.push(new PromotePackagesCommand(selectedPackages, showDisabledCommands));
        }

        const deleteCommands = DeleteCommandHelper.addDeleteCommands(
            UPackKey,
            selectedPackages,
            selectedVersions,
            null /*packageName*/,
            !PermissionHelper.isAdministratorFromRole(role) /*disabled*/,
            cachedPackagesIncluded
        );
        packageCommands.push(...deleteCommands);

        return packageCommands;
    }

    // Get a list of available package commands for this protocol based on the user's role
    public getPackageCommands(
        packageSummary: Package,
        packageVersion: PackageVersion,
        feed: Feed,
        viaPackageList: boolean = false
    ): IPackageCommand[] {
        const packageCommands = new Array<IPackageCommand>();

        if (packageVersion.isDeleted) {
            return packageCommands;
        }

        const role: FeedRole = PermissionHelper.getUsersRoleForFromFeed(feed);
        const showDisabledCommands = role === FeedRole.None || role === FeedRole.Reader;

        // Promote
        packageCommands.push(new PromotePackageCommand(packageSummary, viaPackageList, showDisabledCommands));

        // Delete
        const selectedPackages: Package[] = viaPackageList ? [packageSummary] : null;
        const selectedVersions: PackageVersion[] = viaPackageList ? null : [packageVersion];
        const deleteCommands = DeleteCommandHelper.addDeleteCommands(
            UPackKey,
            selectedPackages,
            selectedVersions,
            packageSummary.name,
            !PermissionHelper.isAdministratorFromRole(role) /*disabled*/
        );
        packageCommands.push(...deleteCommands);

        return packageCommands;
    }

    public supportedFeatures(feed?: Feed): ISupportedFeatures {
        const features: ISupportedFeatures = {
            upstreams: isFeatureFlagEnabled(this.pageContext, "Packaging.Feed.UPackSameCollectionUpstreams", false),
            projectScopedFeeds: this.pageContext
                .getService<HubWebPageDataService>("IFeedWebPageDataService")
                .isProjectScopedUPackEnabled()
        };
        return features;
    }

    // Use supportedFeatures instead.
    // Except for connect to feed dialog that is located in the old Feed/Service/Web location.
    public supportsUpstreams(feed?: Feed): boolean {
        return false;
    }

    public async deleteLatestVersions(
        projectId: string = null /*Project scoped feeds not yet enabled for this protocol */,
        feed: Feed,
        pkgs: Package[]
    ): Promise<void> {
        try {
            const iterator = new TransformingBatchGenerator(
                pkgs,
                this.UPackDataService.batchSize,
                pkg =>
                    <MinimalPackageDetails>{
                        id: pkg.name,
                        version: pkg.versions[0].normalizedVersion
                    }
            );
            await this.UPackDataService.batchDelete(feed, iterator);
        } catch (error) {
            throw new Error(this.key);
        }
    }

    public async deletePackageVersions(
        projectId: string = null /*Project scoped feeds not yet enabled for this protocol */,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ) {
        const iterator = new TransformingBatchGenerator(
            versions,
            this.UPackDataService.batchSize,
            packageVersion =>
                <MinimalPackageDetails>{
                    id: pkg.name,
                    version: packageVersion.normalizedVersion
                }
        );
        await this.UPackDataService.batchDelete(feed, iterator);
    }

    public async restorePackageVersionsToFeed(projectId: string, feed: Feed, pkg: Package, versions: PackageVersion[]) {
        const iterator = new TransformingBatchGenerator(
            versions,
            this.UPackDataService.batchSize,
            packageVersion =>
                <MinimalPackageDetails>{
                    id: pkg.name,
                    version: packageVersion.normalizedVersion
                }
        );
        await this.UPackDataService.batchRestoreToFeed(feed, iterator);
    }

    public async permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void>;
    public async permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        packageVersions: MinimalPackageDetails[]
    ): Promise<void>;
    public async permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        pkgOrDetails: Package | MinimalPackageDetails[],
        versions?: PackageVersion[]
    ) {
        let iterator: IBatchIterator<MinimalPackageDetails[]>;
        if (isPackage(pkgOrDetails)) {
            const pkgName = pkgOrDetails.name;
            iterator = new TransformingBatchGenerator(
                versions,
                this.UPackDataService.batchSize,
                packageVersion => <MinimalPackageDetails>{ id: pkgName, version: packageVersion.normalizedVersion }
            );
        } else {
            iterator = new BatchGenerator(pkgOrDetails, this.UPackDataService.batchSize);
        }

        await this.UPackDataService.batchPermanentlyDelete(feed, iterator);
    }

    public promotePackageVersions(
        projectId: string,
        feed: Feed,
        feedView: FeedView,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void>;
    public promotePackageVersions(
        projectId: string,
        feed: Feed,
        feedView: FeedView,
        packageVersions: MinimalPackageDetails[]
    ): Promise<void>;
    public async promotePackageVersions(
        projectId: string,
        feed: Feed,
        feedView: FeedView,
        pkgOrDetails: Package | MinimalPackageDetails[],
        versions?: PackageVersion[]
    ): Promise<void> {
        let iterator: IBatchIterator<MinimalPackageDetails[]>;
        if (isPackage(pkgOrDetails)) {
            const pkgName = pkgOrDetails.name;
            iterator = new TransformingBatchGenerator(
                versions,
                this.UPackDataService.batchSize,
                packageVersion => <MinimalPackageDetails>{ id: pkgName, version: packageVersion.normalizedVersion }
            );
        } else {
            iterator = new BatchGenerator(pkgOrDetails, this.UPackDataService.batchSize);
        }

        await this.UPackDataService.batchPromote(feed, feedView.id, iterator);
    }

    public getCopyInstallCommand(
        feed: Feed,
        pkgName: string,
        pkgVersion: PackageVersion,
        separator: string = " "
    ): string {
        const endpointUrl = this.getEndpointUrl();
        const projectParam = feed.project
            ? `--project "${feed.project.id}"${separator}` + `--scope project${separator}`
            : "";
        return (
            `az artifacts universal download${separator}` +
            `--organization "${endpointUrl}"${separator}` +
            projectParam +
            `--feed "${feed.name}"${separator}` +
            `--name "${pkgName}"${separator}` +
            `--version "${pkgVersion.version}"${separator}` +
            "--path ."
        );
    }

    // TODO: delete this with old connect to feed code
    private getPublishCommand = (
        feed: Feed,
        pkgName: string,
        version: string,
        pkgDescription: string,
        separator: string
    ): string => {
        const endpointUrl = this.getEndpointUrl();
        const projectParam = feed.project ? `--project "${feed.project.id}"${separator}` : "";
        return (
            `az artifacts universal publish${separator}` +
            `--organization "${endpointUrl}"${separator}` +
            projectParam +
            `--feed "${feed.name}"${separator}` +
            `--name "${pkgName}"${separator}` +
            `--version "${version}"${separator}` +
            `--description "${pkgDescription}"${separator}` +
            "--path ."
        );
    };

    private getEndpointUrl(): string {
        return getServiceLocation(this.pageContext, TfsServiceInstanceId);
    }

    public getUpstreamingBehavior(feed: Feed, pkg: Package): Promise<UpstreamingBehavior> {
        throw new Error(PackageResources.UpstreamBehaviorNotSupportedOnThisProtocol);
    }

    public setUpstreamingBehavior(feed: Feed, pkg: Package, behavior: UpstreamingBehavior): Promise<void> {
        throw new Error(PackageResources.UpstreamBehaviorNotSupportedOnThisProtocol);
    }
}
